import { MessageType, MouseActionType } from "../definitions";
import { IBaseDeviceContext, IDeviceContext, IDeviceInput } from "../mcrc-api";
import { RcContext } from "../context/rcContext";
import { KeyEventMessage, MouseEventMessage, SkinKeyEventMessage } from "../messages/pocket-com";
import { MathHelpers } from "../utils/math";
import { ButtonTracker, MouseEventService } from "./mouseEventService";
import { FeatureToggleService } from "@nx-fe/services";
import { TimeHelpers } from "../utils/time";
import { RcLoggerService } from "./rcLoggerService";
import { BrowserDetectionService } from "./browserDetectionService";
import { OsDetectionService } from "./osDetectionService";
import { ScreenBoundsService } from "./screenBoundsService";
import { Injectable } from "@angular/core";
import { DeviceContext, IRemoteControlStreamingContext } from "../context/deviceContext";

enum AndroidDeviceSkinButtons {
    Home = 3,
    Back = 4,
    VolumeUp = 24,
    VolumeDown = 25,
    LockScreen = 26,
    Menu = 82
}

enum KeyboardModifiers {
    VK_CONTROL = 0x01,
    VK_MENU = 0x02,
    VK_SHIFT = 0x04,
    VK_CAPITAL = 0x40,
    VK_NUMLOCK = 0x80
}

enum KeyboardKeyValues {
    Escape = 27,
    Delete = 46,
    Ctrl = 113 //KeyCode for CTRL_LEFT, hex value : 0x71
}

type KeyboardMessageTypes = MessageType.KEYBOARD_POST | MessageType.KEYBOARD_CTRLED_ALTED_SEND;

type KeyboardSendWithElement = {
    name: string;
    sendWith: KeyboardMessageTypes;
}

const KeyboardSendWith: Record<number, KeyboardSendWithElement> = {
    13: { name: 'Enter', sendWith: MessageType.KEYBOARD_POST },
    27: { name: 'Escape', sendWith: MessageType.KEYBOARD_POST },
    29: { name: 'Ctrl', sendWith: MessageType.KEYBOARD_POST }
}

const KeyboardPreventDefault: Record<number, string> = {
    8: 'Backspace',
    9: 'Tab',
    13: 'Enter',
    27: 'Escape',
    29: 'Ctrl',
    33: 'Page Up',
    34: 'Page Down',
    35: 'End',
    36: 'Home',
    37: 'Left Arrow',
    38: 'Up Arrow',
    39: 'Right Arrow',
    40: 'Down Arrow',
    45: 'Insert',
    46: 'Delete',
    //  65: { name: 'A', value: 29 },
    112: 'F1',
    113: 'F2',
    114: 'F3',
    115: 'F4',
    116: 'F5',
    117: 'F6',
    118: 'F7',
    119: 'F8',
    120: 'F9',
    121: 'F10',
    122: 'F11',
    //123: { name: 'F12', value: 142 },
}

@Injectable({
    providedIn: 'root'
})
export class DeviceInputService implements IDeviceInput {
    static readonly MAX_SHORT = 65535;

    // Constant significant delta relative to minimum stroke width x2 (5px)
    static readonly SIGNIFICANT_EVENT_DELTA = 10;   // (px)
    // Minimum interval for sending mouse events
    static readonly EVENT_SEND_INTERVAL = 40;       // (ms)

    private videoStreaming: boolean;
    private canvas: HTMLCanvasElement;
    private context: IBaseDeviceContext;

    constructor(
        public featureToggleService: FeatureToggleService,
        private mcrcLog: RcLoggerService,
        private browserDetection: BrowserDetectionService,
        private screenBounds: ScreenBoundsService) {
    }

    public get deviceContext(): IBaseDeviceContext {
        return this.context;
    }

    public set deviceContext(context: IBaseDeviceContext) {
        this.context = context;
        // video streaming - initialize DeviceInput (ourContext === undefined)
        this.videoStreaming = (<any>this.context).videoStreaming;
        this.canvas = this.videoStreaming ? document.getElementById('rcInputCanvas') as HTMLCanvasElement : (<IDeviceContext>this.context).canvas.element;
        this.registerKeyInputListeners();
    }

    // Indicates relative significant mouse event threshold,
    // considering the true device resolution to browser canvas one
    private relativeSignificantDeltaX: number;
    private relativeSignificantDeltaY: number;
    // Stores timestamp of when we sent last mouse event (or batch of mouse events)
    private lastSentEventTimestamp = 0;
    // Keeps references of last mouse position within the device screen which we use to complete mouse tracking cycle
    // i.e. have down-move-up or down-up within the device screen.
    // If user moves the mouse outside of the device screen, these events are discarded (not forwarded to the agent)
    private lastInScreenMouseEvent;

    // Last mouse event
    private lastMouseEvent = {};

    // Queue of mouse move events, in case they arrive to quickly
    private mouseMoveEventQueue = [];

    // Tracks which mouse buttons are currently pressed
    private mouseButtonTracker = new ButtonTracker();
    // private bufferingLog = new BufferingConsoleLog(false);
    // Indicates if currently received mouse events should be forwarded to the agent
    private isTrackingMouse = false;
    // var self = this;
    private deviceSkinController = null;
    private zoomController = null;
    static keycodesToAscii = {
        normal: [0, 0, 0, 3, 0, 0, 0, 0, 8, 9, 0, 0, 0, 13, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 0, 0, 0, 0, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 0, 59, 0, 61, 0, 0, 0, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 0, 0, 0, 0, 0, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 42, 43, 0, 45, 46, 47, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 59, 61, 44, 45, 46, 47,  96, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,  91,  92,  93, 39, 0, 0, 0,  92, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        shift:  [0, 0, 0, 3, 0, 0, 0, 0, 8, 9, 0, 0, 0, 13, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 0, 0, 0, 0, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 41, 33, 64, 35, 36, 37, 94, 38, 42, 40, 0, 58, 0, 43, 0, 0, 0, 65, 66, 67,  68,  69,  70,  71,  72,  73,  74,  75,  76,  77,  78,  79,  80,  81,  82,  83,  84,  85,  86,  87,  88,  89,  90, 0, 0, 0, 0, 0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0, 42, 43, 0, 45, 46, 47, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 95, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 58, 43, 60, 95, 62, 63, 126, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 123, 124, 125, 34, 0, 0, 0, 124, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }

    //Initialize keyCodes dictionary to map values for firefox keydown event
    static keycodesToFirefox =
        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 186, 60, 187, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 128, 129, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154, 155, 156, 157, 158, 159, 160, 161, 162, 163, 164, 165, 166, 167, 168, 169, 170, 171, 172, 189, 174, 175, 176, 177, 178, 179, 180, 181, 182, 183, 184, 185, 186, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196, 197, 198, 199, 200, 201, 202, 203, 204, 205, 206, 207, 208, 209, 210, 211, 212, 213, 214, 215, 216, 217, 218, 219, 220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231, 232, 233, 234, 235, 236, 237, 238, 239, 240, 241, 242, 243, 244, 245, 246, 247, 248, 249, 250, 251, 252, 253, 254, 255];

    private lastKeyDown = '';
    // Public Functions
    // Public for skin buttons to use
    public sendSkinKey = (key: number) => {
        this.sendSkinKeyEvent(key, (<any>window.event).ctrlKey);
    }

    // private sendAppKey = (key) => {
    //     this.sendAppKeyEvent(key);
    // }

    private sendButtonEvent = (button) => {
        if (button.keyValue != -1) {
            if (button.type === "VK") {
                this.sendSkinKeyEvent(button.keyValue, (<any>window.event).ctrlKey);
            } else if (button.type === "AK") {
                this.sendAppKeyEvent(button.keyValue);
            }
        }
    }

    // used for debug only
    /* istanbul ignore next */
    private eventToString = (event) => {
        return '(x: ' + (event.offsetX || event.clientX) + ', y: ' + (event.offsetY || event.clientY) + ')';
    }

    public sendKeyboardKey = (keyCode: number, shift: boolean, alt: boolean, control: boolean, num: boolean) => this.sendKeyEvent(keyCode, shift, alt, control, num);

    private sendSkinKeyEvent(key: number, ctrl: boolean) {
        if (!RcContext.instance.security.RemoteControlInput) {
            return;
        }

        const keyboardModifiers = (ctrl ? KeyboardKeyValues.Ctrl : 0);

        const msg = new SkinKeyEventMessage(key, keyboardModifiers);

        if (this.featureToggleService.isEnabled("DeveloperMode")) {
            this.mcrcLog.debug('Skin Button Pressed...')
            this.mcrcLog.debug(`Message Type: ${MessageType[MessageType.KEYBOARD_SEND]} with value ${MessageType.KEYBOARD_SEND}, Key Pressed:  ${Object.keys(AndroidDeviceSkinButtons).filter(k => AndroidDeviceSkinButtons[k] === key)}, Is CTRL Pressed: ${ctrl}`);
        }

        RcContext.instance.session.sendMessage(msg);
    }

    public registerKeyInputListeners = () => {
        document.addEventListener("keydown", this.onKeyDown, false);
        document.addEventListener("keyup", this.onKeyUp, false);
        document.addEventListener("keypress", this.onKeyPress, false);
    }

    public unregisterKeyInputListeners = () => {
        document.removeEventListener("keydown", this.onKeyDown, false);
        document.removeEventListener("keyup", this.onKeyUp, false);
        document.removeEventListener("keypress", this.onKeyPress, false);
    }

    public registerMouseInputListeners(canvas: HTMLElement): void {
        if ("ontouchstart" in window) {
            canvas.addEventListener('touchstart', this.onTouchEvent, false);
            canvas.addEventListener('touchend', this.onTouchEvent, false);
            canvas.addEventListener('touchcancel', this.onTouchEvent, false);
            canvas.addEventListener('touchmove', this.onTouchEvent, false);
        } else if ((<any>navigator).msMaxTouchPoints) {
            canvas.addEventListener('pointerdown', this.onMouseDown, false);
            canvas.addEventListener('pointerup', this.onMouseUp, false);
            canvas.addEventListener('pointerout', this.onMouseUp, false);
            canvas.addEventListener('pointermove', this.onMouseMove, false);
        }
        canvas.addEventListener('mousedown', this.onMouseDown, false);
        canvas.addEventListener('contextmenu', this.preventContextMenu, false);
        canvas.addEventListener('mouseup', this.onMouseUp, false);
        canvas.addEventListener('mouseout', (event) => {
            if (!!event.relatedTarget) {
                this.onMouseUp(event);
            }
        }, false);
        canvas.addEventListener('mousemove', this.onMouseMove, false);
    }

    public unregisterMouseInputListeners(canvas: HTMLElement) {
        if ("ontouchstart" in window) {
            canvas.removeEventListener('touchstart', this.onTouchEvent, false);
            canvas.removeEventListener('touchend', this.onTouchEvent, false);
            canvas.removeEventListener('touchcancel', this.onTouchEvent, false);
            canvas.removeEventListener('touchmove', this.onTouchEvent, false);
        } else if ((<any>navigator).msMaxTouchPoints) {
            canvas.removeEventListener('pointerdown', this.onMouseDown, false);
            canvas.removeEventListener('pointerup', this.onMouseUp, false);
            canvas.removeEventListener('pointerout', this.onMouseUp, false);
            canvas.removeEventListener('pointermove', this.onMouseMove, false);
        }
        canvas.removeEventListener('mousedown', this.onMouseDown, false);
        canvas.removeEventListener('contextmenu', this.preventContextMenu, false);
        canvas.removeEventListener('mouseup', this.onMouseUp, false);
        canvas.removeEventListener('mouseout', this.onMouseUp, false);
        canvas.removeEventListener('mousemove', this.onMouseMove, false);
    }

    public setSkinController = (skinController) => this.deviceSkinController = skinController;

    public getSkinController = () => this.deviceSkinController;

    public setZoomController = (zoomingController): HTMLCanvasElement => {
        this.zoomController = zoomingController;
        return this.canvas;
    }

    // Private functions

    private getRotation = () => {
        // JL: HD-608 - No rotation for projected screen
        return this.context.deviceSupportsScreenProjection ? 0 : this.context.rotation;
    }

    private sendAppKeyEvent = (key: number) => {
        if (!RcContext.instance.security.RemoteControlInput) {
            return;
        }
        const msg = new KeyEventMessage(MessageType.PC_APPBUTTON, key);

        RcContext.instance.session.sendMessage(msg);
    }

    private sendKeyEvent(key: number, shift: boolean, alt: boolean, ctrl: boolean, num: boolean) {
        if (!RcContext.instance.security.RemoteControlInput) {
            return;
        }

        let type: KeyboardMessageTypes = MessageType.KEYBOARD_CTRLED_ALTED_SEND;

        if (!OsDetectionService.IsWindows(this.context.osType)) {
            const mapping = KeyboardSendWith[key];
            if (mapping?.sendWith !== undefined) {
                type = mapping.sendWith;
            }
        }

        const keyboardModifiers = (shift ? KeyboardModifiers.VK_SHIFT : 0) |
            (alt ? KeyboardModifiers.VK_MENU : 0) |
            (ctrl ? KeyboardModifiers.VK_CONTROL : 0) |
            (num ? KeyboardModifiers.VK_NUMLOCK : 0);

        const msg = new KeyEventMessage(type, key, keyboardModifiers);

        RcContext.instance.session.sendMessage(msg);
    }

    private postKeyEvent(key: MessageType, shift: boolean, alt: boolean, ctrl: boolean) {
        if (!RcContext.instance.security.RemoteControlInput) {
            return;
        }

        const msg = new KeyEventMessage(MessageType.KEYBOARD_POST, key);
        RcContext.instance.session.sendMessage(msg);
    }

    /*
    private getEventCoordinates = (event) => {
        var x = 0, y = 0;
        if (event.webGLEvent === undefined) {
            x = (MouseEventService.getTransformedMousePositionX(event, rotationInRadians, this.context.scale) - offsetX) / this.context.skinScreenWidth * DeviceInput.MAX_SHORT;
            y = (MouseEventService.getTransformedMousePositionY(event, rotationInRadians, this.context.scale) - offsetY) / this.context.skinScreenHeight * DeviceInput.MAX_SHORT;
        } else {
            x = event.agentX;
            y = event.agentY;
        }
        return [x, y];
    }
    //*/

    private sendMousePacket(type: MouseActionType, event: any) {
        let isAllowed = this.isAllowedSendMouseEvent(event);
        if (!isAllowed) {
            return;
        }

        MouseEventService.fixEvent(event);

        //context.canvas.projected = this.context.deviceSupportsScreenProjection;
        let x;
        let y;
        if (this.videoStreaming) {
            let streamingContext = <IRemoteControlStreamingContext>this.context;
            if (event.type != 'mouseout' && event.type != 'pointerout') {
                x = event.offsetX || (event.pageX - event.currentTarget.offsetLeft);
                y = event.offsetY || (event.pageY - event.currentTarget.offsetTop);
                const pos = streamingContext.deviceViewHandler.convertToDeviceCoordinates(x, y);
                const isMouseup = event.type == 'mouseup';
                if (pos.x < 0 && !isMouseup) {
                    return;
                }

                x = pos.x * DeviceInputService.MAX_SHORT;
                y = pos.y * DeviceInputService.MAX_SHORT;
            }
        } else {
            let standardContext = <DeviceContext>this.context;
            if (event.webGLEvent === undefined) {
                var rotationInRadians = MouseEventService.getRotationInRadians((this.getRotation() + standardContext.clientSideRotation) & 3);
                var offsetX = standardContext.screenOffsetX;
                var offsetY = standardContext.screenOffsetY;

                if (this.context.deviceSupportsScreenProjection && (standardContext.screen.rotation + this.context.rotation + standardContext.clientSideRotation) != 0) {
                    var offset = MathHelpers.getProjectionOffset(this.context);
                    offsetX = offset.x;
                    offsetY = offset.y;
                }

                x = (MouseEventService.getTransformedMousePositionX(event, rotationInRadians, standardContext.targetScale) - offsetX) / standardContext.skinScreenWidth * DeviceInputService.MAX_SHORT;
                y = (MouseEventService.getTransformedMousePositionY(event, rotationInRadians, standardContext.targetScale) - offsetY) / standardContext.skinScreenHeight * DeviceInputService.MAX_SHORT;
            } else {
                x = event.agentX;
                y = event.agentY;
            }
        }

        // this.mcrcLog.debug("x: " + x + " y: " + y);
        if (event.type == "mouseout" || event.type == "pointerout") {
            // mouseout will always be outside the bounds of the screen, so we clamp it to the bounds
            if (x < 0) x = 0;
            if (x > DeviceInputService.MAX_SHORT) x = DeviceInputService.MAX_SHORT;
            if (y < 0) y = 0;
            if (y > DeviceInputService.MAX_SHORT) y = DeviceInputService.MAX_SHORT;
        }
        else {
            if (x < 0 || x > DeviceInputService.MAX_SHORT) return; // is it in the bounds of the screen?
            if (y < 0 || y > DeviceInputService.MAX_SHORT) return;
        }
        if ((this.getRotation() != 0 && this.getRotation() != 2)) {
            // swap x,y for landscape
            var t = x;
            x = y | 0; // Or by zero for fast convert to int
            y = t | 0;
            if (this.getRotation() == 1) y = DeviceInputService.MAX_SHORT - y; // flip y for landscape cw
            if (this.getRotation() == 3) x = DeviceInputService.MAX_SHORT - x; // flip x for landscape ccw
        } else if (this.getRotation() == 2) {
            y = DeviceInputService.MAX_SHORT - y;
            x = DeviceInputService.MAX_SHORT - x;
        }

        const msg = new MouseEventMessage(type, x, y)
        RcContext.instance.session.sendMessage(msg);
    }

    private onSkinButtonPressed = (buttonData) => {
        this.sendButtonEvent(buttonData);
    }

    private preventContextMenu = (event) => {
        if (event.preventDefault) {
            event.preventDefault();
        }
    }

    private sendMouseDown = (event: any) => {
        this.relativeSignificantDeltaX = DeviceInputService.SIGNIFICANT_EVENT_DELTA * this.context.scale;
        this.relativeSignificantDeltaY = DeviceInputService.SIGNIFICANT_EVENT_DELTA * this.context.scale;
        //bufferingLog.log('Significant deltas: ' + JSON.stringify({x: relativeSignificantDeltaX, y: relativeSignificantDeltaY}))

        this.mouseButtonTracker.trackMouseDown(event);
        this.isTrackingMouse = true;

        if (this.isWithinScreenEvent(event)) {
            if (this.mouseButtonTracker.hasLeftChanged()) {
                this.sendMousePacket(MouseActionType.MOUSEDOWN, event);
            }

            if (this.mouseButtonTracker.hasRightChanged()) {
                this.sendMousePacket(MouseActionType.RMOUSEDOWN, event);
            }

            this.lastMouseEvent = event;
            //bufferingLog.log('MOUSE_DOWN');
        }
    }

    private sendMouseUp(event) {
        this.mouseButtonTracker.trackMouseUp(event);
        this.isTrackingMouse = false;

        // This handler gets invoked when either of these events occur:
        // 1. mouseup occurs within canvas element. In this case
        //    we simply forward mouse event for the corresponding button
        //    to the the agent, if event is in screen.
        //    If even is outside of the screen this event will be dropped by
        //    the agent anyway, so we don't send anything. The
        //    completeOrRestartGestureIfRequired should have handled this already
        //    by sending fake mouse-up when mouse was crossing the screen boundary.
        // 2. mouseout indicates user left canvas element possibly
        //    while still being within the device screen and mouse button could be either up
        //    or down, when crossing the boundary. Ex: device is zoomed-in and takes-up
        //    entire area of the canvas element while user mouse enters extended toolbar).
        //    Since in this case mouse button may still be down and as a result, last
        //    recorded in-screen event is mousedown as well, we need to enforce
        //    sending fake mouse up when leaving the screen, so we change event type to
        //    mouseup explicitly. This ensures agent and browser mouse events will be
        //    synchronized on completing current mouse down-move-up sequence. If mouse
        //    is up then hasLeftChanged will indicate that and still send corresponding
        //    up event. If mouseup was triggered earlier this function will not emit any
        //    events since no mouse gesture in progress. In case if user enters canvas
        //    element when mouse is down and was for the whole time after leaving
        //    canvas element then the sendMouseMove will be invoked and
        //    completeOrRestartGestureIfRequired will resynchronize mouse states
        //    by sending fake mouse down to restart the gesture (or start new up-move-d0own).
        if (this.isWithinScreenEvent(event)) {
            if (this.mouseButtonTracker.hasLeftChanged() || this.mouseButtonTracker.isLeftDown()) {
                this.sendMousePacket(MouseActionType.MOUSEUP, event);
            }

            if (this.mouseButtonTracker.hasRightChanged() || this.mouseButtonTracker.isRightDown()) {
                this.sendMousePacket(MouseActionType.RMOUSEUP, event);
            }

            this.lastMouseEvent = event;
        } else {
            if (this.lastInScreenMouseEvent) {
                this.lastInScreenMouseEvent.type = 'mouseup';

                if (this.mouseButtonTracker.hasLeftChanged() || this.mouseButtonTracker.isLeftDown()) {
                    this.sendMousePacket(MouseActionType.MOUSEUP, this.lastInScreenMouseEvent);
                }
                if (this.mouseButtonTracker.hasRightChanged() || this.mouseButtonTracker.isRightDown()) {
                    this.sendMousePacket(MouseActionType.RMOUSEUP, this.lastInScreenMouseEvent);
                }
            }
        }

        //bufferingLog.logAndFlush('MOUSE_UP');
    }

    // Significant change is a change which would be visible
    // on both browser and device screen. For now it's constant relative
    // to the minimum brush size x2 in the annotations feature
    private isSignificantChange = (previousEvent, currentEvent) => {
        var previousX = previousEvent.offsetX || previousEvent.clientX;
        var previousY = previousEvent.offsetY || previousEvent.clientY;
        var currentX = currentEvent.offsetX || currentEvent.clientX;
        var currentY = currentEvent.offsetY || currentEvent.clientY;

        return (Math.abs(currentX - previousX) >= this.relativeSignificantDeltaX ||
            Math.abs(currentY - previousY) >= this.relativeSignificantDeltaY);
    }

    // Checks if EVENT_SEND_INTERVAL passed, and when it is will enter new interval
    // returns indication of whether an interval passed or not
    private isSendWindowIntervalReached = () => {
        var timestamp = TimeHelpers.getTimestamp();
        return (timestamp - this.lastSentEventTimestamp) > DeviceInputService.EVENT_SEND_INTERVAL;
    }

    private flushMouseEventQueue = () => {
        // Because this is called from timer code as well as mouse event handler
        // timer just fires on the interval but if mouse event handler already
        // flushed the queue and interval not reached we want to ensure this here
        if (this.isSendWindowIntervalReached() && this.mouseMoveEventQueue.length > 0) {
            //bufferingLog.log('MOUSE_MOVE: Sending batch of size: ' + mouseMoveEventQueue.length);
            // TODO 2025.1: in future implement batch event to send multiple mouse events sending here
            // for now just sending the last one in the batch and batch will be dropped
            this.sendMousePacket(MouseActionType.MOUSEMOVE, this.mouseMoveEventQueue[this.mouseMoveEventQueue.length - 1]);
            this.mouseMoveEventQueue = [];
            this.lastSentEventTimestamp = TimeHelpers.getTimestamp();
        }
    }

    private sendMouseMove = (event) => {

        // Track events for the purpose of which
        // mouse button is currently pressed
        if (event !== undefined) {
            this.mouseButtonTracker.trackMouseMove(event);
        }

        this.completeOrRestartGestureIfRequired(event);

        if (this.isWithinScreenEvent(event)) {
            if (this.isSignificantChange(this.lastMouseEvent, event)) {
                //bufferingLog.log('MOUSE_MOVE: ++ Significant change ' + eventToString(event));
                if (this.isSendWindowIntervalReached()) {
                    // if queue is not empty
                    if (this.mouseMoveEventQueue.length > 0) {
                        //bufferingLog.log('MOUSE_MOVE: Queueing event ' + eventToString(event));
                        // add event at the end of queue
                        this.mouseMoveEventQueue.push(event);
                        // flush queue by sending the batch
                        this.flushMouseEventQueue();
                    } else {
                        // if queue is empty, just send single event immediately
                        //bufferingLog.log('MOUSE_MOVE: Sending immediate event ' + eventToString(event));
                        this.sendMousePacket(MouseActionType.MOUSEMOVE, event);
                        this.lastSentEventTimestamp = TimeHelpers.getTimestamp();
                    }
                } else {
                    //bufferingLog.log('MOUSE_MOVE: Queueing event ' + eventToString(event));
                    // just queue the event
                    this.mouseMoveEventQueue.push(event);
                }
                // capture last significant event as reference for the next one
                this.lastMouseEvent = event;
            } // else change is not significant, just drop it

            //bufferingLog.log('MOUSE_MOVE: -- Dropping non-significant change ' + eventToString(event));
        }
    }

    // Implements check of whether mouse event is occurring withing the device screen boundaries
    private isWithinScreenEvent = (event) => {
        if (this.videoStreaming) {

            MouseEventService.fixEvent(event);
            const x = event.offsetX || (event.pageX - event.currentTarget.offsetLeft);
            const y = event.offsetY || (event.pageY - event.currentTarget.offsetTop);
            return (<IRemoteControlStreamingContext>this.context).deviceViewHandler.convertToDeviceCoordinates(x, y).x >= 0;
        }

        let standardContext = <DeviceContext>this.context;
        const isIrregularSkin = RcContext.instance.deviceContextHandler.currentContext()?.skin.isIrregular;
        return this.screenBounds.isWithinScreenBounds(standardContext, event, isIrregularSkin);
    }

    // Emits fake mouse-down/mouse-up events to complete the event when mouse leaves
    // the screen or restarts gesture if mouse enters screen while mouse down
    // This is because agent will discard processing any mouse events when mouse is outside of device
    // screen in the HTML5 RC view, so if user releases mouse outside of the device screen, this event
    // will not be processed and as a result mouse state in the browser and on the agent will go out of sync.
    private completeOrRestartGestureIfRequired = (event) => {
        // If it's a mousedown then also start tracking by sending mousedown with last in-screen coordinates
        if (this.isWithinScreenEvent(event)) {
            this.lastInScreenMouseEvent = MouseEventService.cloneMouseEventAsType(event);

            // If we are not tracking mouse but entered screen with mouse left or right buttons down
            // then send mouse down to resume drawing
            if (!this.isTrackingMouse && (this.mouseButtonTracker.isLeftDown() || this.mouseButtonTracker.isRightDown())) {
                //bufferingLog.log('Fake Mouse DOWN');
                this.lastInScreenMouseEvent = MouseEventService.cloneMouseEventAsType(event);
                this.lastInScreenMouseEvent.type = 'mousedown';
                if (this.mouseButtonTracker.isLeftDown()) {
                    this.sendMousePacket(MouseActionType.MOUSEDOWN, this.lastInScreenMouseEvent);
                } else if (this.mouseButtonTracker.isRightDown()) {
                    this.sendMousePacket(MouseActionType.RMOUSEDOWN, this.lastInScreenMouseEvent);
                }
                this.isTrackingMouse = true;
            }
        } else {
            // If mouse is not within screen bounds then stop tracking by sending mouse-up event with last in-screen coordinates
            if (this.isTrackingMouse && this.lastInScreenMouseEvent && (this.mouseButtonTracker.isLeftDown() || this.mouseButtonTracker.isRightDown())) {
                //bufferingLog.log('Fake Mouse UP');
                // send mouse up to complete current mouse tracking cycle
                this.lastInScreenMouseEvent.type = 'mouseup';
                if (this.mouseButtonTracker.isLeftDown()) {
                    this.sendMousePacket(MouseActionType.MOUSEUP, this.lastInScreenMouseEvent);
                } else if (this.mouseButtonTracker.isRightDown()) {
                    this.sendMousePacket(MouseActionType.RMOUSEUP, this.lastInScreenMouseEvent);
                }
                this.isTrackingMouse = false;
            }
        }
    }

    private onMouseDown = (event) => {
        // Deselect element that has focus so that keypresses go to device instead
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (this.context.disableMouseInput) {
            return;
        }

        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.stopPropagation) {
            event.stopPropagation();
        }

        if (event.preventDefault) {
            event.preventDefault();
        }

        if (this.zoomController === null || !this.zoomController.handleEvent(event)) {
            if (this.canvas.style.display != 'none') {
                let skinButtonPressed = false;

                if (this.deviceSkinController != null) {
                    skinButtonPressed = this.deviceSkinController.onClick(this.onSkinButtonPressed, event);
                }

                if (!skinButtonPressed) {
                    this.sendMouseDown(event);
                }
            }
            if (event.stopPropagation) {
                event.stopPropagation();
            }
        }
    }

    private onMouseUp = (event) => {

        if (this.context.disableMouseInput) {
            return;
        }
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.stopPropagation) {
            event.stopPropagation();
        }
        if (this.zoomController === null || !this.zoomController.handleEvent(event)) {
            if (this.canvas.style.display !== 'none') {
                this.sendMouseUp(event);
            }
        }
    }

    private onMouseMove = (event) => {
        if (this.context.disableMouseInput) {
            return;
        }
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.stopPropagation) {
            event.stopPropagation();
        }
        if (event.preventDefault) {
            event.preventDefault();
        }
        if (this.zoomController === null || !this.zoomController.handleEvent(event)) {
            if (this.canvas.style.display != 'none') {
                if (this.deviceSkinController != null) {
                    this.deviceSkinController.onHover(event);
                }
                this.sendMouseMove(event);
            }
        }
    }

    private onTouchEvent = (event) => {

        var touches = event.changedTouches,
            first = touches[0],
            type = "";
        switch (event.type) {
            case "touchstart":
                type = "mousedown";
                break;
            case "touchmove":
                type = "mousemove";
                break;
            case "touchcancel":
            case "touchend":
                type = "mouseup";
                break;
            default:
                return;
        }

        const simulatedEvent = {
            target: first.target,
            pageX: first.pageX,
            pageY: first.pageY,
            clientX: first.clientX,
            clientY: first.clientY,
            screenX: first.screenX,
            screenY: first.screenY,
            type: type,
            which: 1
        };

        switch (event.type) {
            case "touchstart":
                this.onMouseDown(simulatedEvent);
                break;
            case "touchmove":
                this.onMouseMove(simulatedEvent);
                break;
            case "touchcancel":
            case "touchend":
                this.onMouseUp(simulatedEvent);
                break;
            default:
                return;
        }

        event.preventDefault();
    }

    private handleKeyEvent(event: any) {

        // if key press going to a selected element then keypress should go that element and should not be handled
        if (event.target.nodeName != 'BODY') {
            return;
        }
        if (this.zoomController === null || !this.zoomController.handleEvent(event)) {
            var keyCode = event.keyCode;

            if (window.event) {
                keyCode = (<any>window.event).keyCode;
            }
            if (typeof KeyboardPreventDefault[keyCode] != "undefined" || event.altKey || event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
            }
            var key = event.key;
            var useKeyDown = event.type == "keydown" &&
                ((keyCode in KeyboardPreventDefault) ||  // special keys (backspace, delete, etc.)
                    ((event.altKey || event.ctrlKey) && keyCode != 17 && keyCode != 18)); // use keydown for alt/ctrl modified keys but do not send ctrl or alt on their own

            if (OsDetectionService.IsWindowsCe(this.context.osType) || OsDetectionService.IsWindows(this.context.osType) || OsDetectionService.IsIOS(this.context.osType)) {
                if (key && key.length == 1) {
                    if (this.lastKeyDown == key) { // to prevent keys being emitted twice by keydown and keypress (keycode is different for keydown and keypress)
                        useKeyDown = true;
                    } else {
                        var asciiValue;
                        if (event.shiftKey) {
                            asciiValue = DeviceInputService.keycodesToAscii["shift"][event.keyCode];
                        } else {
                            asciiValue = DeviceInputService.keycodesToAscii["normal"][event.keyCode];
                        }

                        // Refactoring is required for keycodesToAscii mapper
                        // Need to get rid of this dictionary, as some values are different in different browsers
                        // e.g "Minus" have keyCode 189 in Chrome and 173 in FireFox
                        // which may cause another bugs

                        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode

                        useKeyDown = (event.keyCode < 255) && asciiValue == event.key.charCodeAt(0);
                        this.lastKeyDown = key;

                        //suppress keyCode values of firefox keydown event
                        if (this.browserDetection.isFirefox()) {
                            keyCode = DeviceInputService.keycodesToFirefox[keyCode];
                        }
                    }
                }
            }

            if (useKeyDown) {
                if (event.type == "keydown") {
                    if (keyCode != 16 && keyCode != 17 && keyCode != 18) { // don't do ctrl alt shift

                        // Parameter shows that Num Lock modifier is activated in the KeyboardEvent
                        var numLockState = event.getModifierState("NumLock");
                        var isNumPadKey = event.code.substr(0, 6) === "Numpad";

                        // Applying keymodified "NumLock", only on numpad keys with NumLock state On
                        var numKey = numLockState && isNumPadKey;

                        if (this.mcrcLog.isDebugLogEnabled) {
                            const keyName = keyCode in KeyboardPreventDefault ? KeyboardPreventDefault[keyCode] : String.fromCharCode(keyCode);
                            this.mcrcLog.debug(`Key Down: ${keyName} Shift: ${event.shiftKey} Alt: ${event.altKey} Ctrl: ${event.ctrlKey}`);
                            this.mcrcLog.debug("Keydown: " + event.key);
                        }
                        this.sendKeyEvent(keyCode, event.shiftKey, event.altKey, event.ctrlKey, numKey);
                    }
                }
            }
        }
    }

    private handleKeyPress(event) {
        // if key press going to a selected element then keypress should go that element and should not be handled
        if (event.target.nodeName != 'BODY') {
            return;
        }

        if (this.zoomController === null || !this.zoomController.handleEvent(event)) {

            if (event.altKey || event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
            }
            var key = event.key;
            var useKeyDown = false; // use keydown for alt/ctrl modified keys but do not send ctrl or alt on their own

            if (OsDetectionService.IsWindowsCe(this.context.osType) || OsDetectionService.IsWindows(this.context.osType) || OsDetectionService.IsIOS(this.context.osType)) {
                if (key && key.length == 1) {
                    if (this.lastKeyDown == key) { // to prevent keys being emitted twice by keydown and keypress (keycode is different for keydown and keypress)
                        useKeyDown = true;
                    } else {
                        var asciiValue;
                        if (event.shiftKey) {
                            asciiValue = DeviceInputService.keycodesToAscii["shift"][event.keyCode];
                        } else {
                            asciiValue = DeviceInputService.keycodesToAscii["normal"][event.keyCode];
                        }
                        useKeyDown = (event.keyCode < 255) && asciiValue == event.key.charCodeAt(0);
                        this.lastKeyDown = key;
                    }
                }
            }
            if (!useKeyDown && event.type == "keypress") {
                this.mcrcLog.debug('Key Pressed: ' + event.which + ' Shift: ' + event.shiftKey + ' Alt: ' + event.altKey + ' Ctrl: ' + event.ctrlKey);
                this.postKeyEvent(event.key.charCodeAt(0), event.shiftKey, event.altKey, event.ctrlKey);
            }
        }
    }

    private onKeyDown = (event) => this.handleKeyEvent(event);

    private onKeyUp = (event) => this.handleKeyEvent(event);

    private onKeyPress = (event) => this.handleKeyPress(event);

    private isAllowedSendMouseEvent = (event) => {
        if (this.deviceSkinController && this.deviceSkinController.isAllowedSendMouseEvent) {
            return this.deviceSkinController.isAllowedSendMouseEvent(event);
        }

        return RcContext.instance.security.RemoteControlInput;
    }
};
